// Tour Navigation Helpers
// Functions to help tours navigate between different pages/steps

import { currentStep as benchmarkingStep } from '../state/benchmarkingWorkflowState.js'

/**
 * Navigate to a specific Benchmarking workflow step
 * @param {number} stepNumber - Step number (1-3)
 */
export function navigateToBenchmarkingStep(stepNumber) {
  if (benchmarkingStep.value !== stepNumber) {
    benchmarkingStep.value = stepNumber
    console.log(`[Tour] Navigated to Benchmarking Step ${stepNumber}`)
  }
}

/**
 * Navigate to URL and wait for a sentinel element to appear.
 * Uses pushState + popstate (SPA navigation, no full reload).
 * The router.jsx monkey-patches pushState to trigger wouter re-renders.
 *
 * @param {string} url - Target URL (e.g., '/cells?view=grid')
 * @param {string} sentinel - CSS selector confirming the page is ready
 * @param {Object} [options]
 * @param {number} [options.timeout=8000] - Max wait time in ms
 * @returns {Promise<Element|null>}
 */
export async function navigateToPage(url, sentinel, { timeout = 8000 } = {}) {
  const currentUrl = new URL(window.location)
  const targetUrl = new URL(url, window.location.origin)

  if (currentUrl.pathname !== targetUrl.pathname ||
      currentUrl.search !== targetUrl.search) {
    window.history.pushState({}, '', url)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  return waitForElement(sentinel, { timeout })
}

/**
 * Wait for an element to appear in the DOM using MutationObserver
 * @param {string} selector - CSS selector
 * @param {Object} [options] - Options object
 * @param {number} [options.timeout=5000] - Max wait time in ms
 * @returns {Promise<Element|null>} Resolves with element or null on timeout (soft-fail)
 */
export function waitForElement(selector, { timeout = 5000 } = {}) {
  return new Promise((resolve) => {
    // Check immediately before creating observer
    const existing = document.querySelector(selector)
    if (existing) {
      resolve(existing)
      return
    }

    let timeoutId
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector)
      if (el) {
        clearTimeout(timeoutId)
        observer.disconnect()
        resolve(el)
      }
    })

    observer.observe(document.body, { childList: true, subtree: true })

    timeoutId = setTimeout(() => {
      observer.disconnect()
      console.warn(`[Tour] waitForElement timed out: "${selector}" after ${timeout}ms`)
      resolve(null) // Soft-fail: resolve null so tour can show centered tooltip
    }, timeout)
  })
}

/**
 * Scroll element into view with smooth animation
 * @param {string} selector - CSS selector
 * @returns {Promise<Element|null>}
 */
export async function scrollToElement(selector) {
  const element = await waitForElement(selector, { timeout: 2000 })
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
  } else {
    console.warn(`[Tour] Could not scroll to ${selector}: element not found`)
  }
  return element
}
